import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Upload, Loader2, Check, Film, Clock } from 'lucide-react';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { cmsMediaApi, type AiDisclosure, type CmsMediaItem } from '../../lib/api/cms';
import { AiDisclosureBadge, AiDisclosureDialog } from './AiDisclosure';
import { cn } from '../../lib/utils';

type MediaKind = 'image' | 'video';

interface MediaPickerProps {
  open: boolean;
  onClose: () => void;
  onSelect: (url: string) => void;
  /** Which asset kind to browse/upload. Defaults to images. */
  kind?: MediaKind;
}

const IMAGE_TYPES = ['image/png', 'image/jpeg', 'image/webp'];
const VIDEO_TYPES = ['video/mp4', 'video/quicktime', 'video/webm', 'video/x-m4v'];

/** Playback URL for a video asset (HLS master playlist, else the raw source). */
const videoUrl = (item: CmsMediaItem): string => item.hls_playlist_url || item.public_url || '';

export const MediaPicker: React.FC<MediaPickerProps> = ({ open, onClose, onSelect, kind = 'image' }) => {
  const isVideo = kind === 'video';
  const acceptTypes = isVideo ? VIDEO_TYPES : IMAGE_TYPES;
  const [items, setItems] = useState<CmsMediaItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [search, setSearch] = useState('');
  // Frisch hochgeladenes Medium: erst KI-Kennzeichnung klären, dann übernehmen.
  const [pendingUpload, setPendingUpload] = useState<CmsMediaItem | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await cmsMediaApi.list({ visibility: 'public', q: search || undefined, limit: 100 });
      setItems(
        res.items.filter((i) =>
          isVideo ? i.media_kind === 'video' : IMAGE_TYPES.includes(i.content_type) && !!i.public_url,
        ),
      );
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Medien konnten nicht geladen werden');
    } finally {
      setLoading(false);
    }
  }, [search, isVideo]);

  useEffect(() => {
    if (open) void load();
  }, [open, load]);

  const handleFiles = async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    const file = files[0];
    if (!acceptTypes.includes(file.type)) {
      toast.error(isVideo ? 'Nur MP4, MOV oder WebM werden unterstützt' : 'Nur PNG, JPEG oder WebP werden unterstützt');
      return;
    }
    setUploading(true);
    try {
      const item = await cmsMediaApi.upload(file, { title: file.name });
      toast.success(
        isVideo
          ? 'Video hochgeladen – Verarbeitung läuft. Es kann nach der Transkodierung ausgewählt werden.'
          : 'Bild hochgeladen',
      );
      // Erst die KI-Kennzeichnung klären; das Übernehmen passiert danach in
      // `resolvePendingUpload`, sonst wäre der Dialog nach dem Schließen weg.
      setPendingUpload(item);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Upload fehlgeschlagen');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const resolvePendingUpload = async (value: AiDisclosure) => {
    const item = pendingUpload;
    setPendingUpload(null);
    if (!item) return;
    if (value !== 'none') {
      try {
        await cmsMediaApi.setAiDisclosure(item.id, value);
      } catch (e) {
        toast.error(e instanceof Error ? e.message : 'KI-Kennzeichnung konnte nicht gesetzt werden');
      }
    }
    // Videos sind erst nach der Transkodierung verwendbar — nur neu laden.
    if (item.media_kind === 'video' || !item.public_url) {
      await load();
      return;
    }
    onSelect(item.public_url);
    onClose();
  };

  const pickVideo = (item: CmsMediaItem) => {
    if (item.processing_status !== 'ready') {
      toast.error('Video wird noch verarbeitet und kann noch nicht verwendet werden.');
      return;
    }
    onSelect(videoUrl(item));
    onClose();
  };

  return (
    <>
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent size="2xl">
        <DialogHeader>
          <DialogTitle>Medienbibliothek</DialogTitle>
          <DialogDescription>
            {isVideo ? 'Video auswählen oder neues hochladen.' : 'Bild auswählen oder neues hochladen.'}
          </DialogDescription>
        </DialogHeader>

        <div className="flex items-center gap-2">
          <Input
            placeholder="Suchen…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') void load(); }}
          />
          <input
            ref={fileInputRef}
            type="file"
            accept={acceptTypes.join(',')}
            className="hidden"
            onChange={(e) => void handleFiles(e.target.files)}
          />
          <Button
            type="button"
            variant="outline"
            loading={uploading}
            onClick={() => fileInputRef.current?.click()}
          >
            <Upload className="mr-2 h-4 w-4" /> Hochladen
          </Button>
        </div>

        <div className="max-h-[55vh] overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center py-12 text-muted-foreground">
              <Loader2 className="h-6 w-6 animate-spin" />
            </div>
          ) : items.length === 0 ? (
            <p className="py-12 text-center text-sm text-muted-foreground">
              {isVideo
                ? 'Keine Videos vorhanden. Lade ein neues Video hoch.'
                : 'Keine Bilder vorhanden. Lade ein neues Bild hoch.'}
            </p>
          ) : isVideo ? (
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
              {items.map((item) => {
                const ready = item.processing_status === 'ready';
                return (
                  <button
                    key={item.id}
                    type="button"
                    className={cn(
                      'group relative flex aspect-video flex-col items-center justify-center gap-2 overflow-hidden rounded-md border bg-muted/30 p-2 text-center',
                      ready ? 'hover:ring-2 hover:ring-primary' : 'cursor-not-allowed opacity-70',
                    )}
                    onClick={() => pickVideo(item)}
                    title={item.title || item.original_filename}
                  >
                    {item.thumbnail_url ? (
                      <img src={item.thumbnail_url} alt="" className="absolute inset-0 h-full w-full object-cover" />
                    ) : null}
                    <span className="relative flex flex-col items-center gap-1">
                      {ready ? (
                        <Film className="h-6 w-6 text-primary" />
                      ) : (
                        <Clock className="h-6 w-6 animate-pulse text-muted-foreground" />
                      )}
                      <span className="line-clamp-1 max-w-full text-xs text-muted-foreground">
                        {item.title || item.original_filename}
                      </span>
                      {!ready && (
                        <span className="text-[11px] text-muted-foreground">
                          {item.processing_status === 'failed' ? 'Fehlgeschlagen' : 'Wird verarbeitet…'}
                        </span>
                      )}
                    </span>
                    <AiDisclosureBadge value={item.ai_disclosure} />
                  </button>
                );
              })}
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4">
              {items.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  className={cn(
                    'group relative aspect-square overflow-hidden rounded-md border bg-muted/30',
                    'hover:ring-2 hover:ring-primary',
                  )}
                  onClick={() => { onSelect(item.public_url || ''); onClose(); }}
                  title={item.title || item.original_filename}
                >
                  <img
                    /* Vorschaufassung statt Original — siehe CmsMediaLibrary. */
                    src={item.thumbnail_url || item.public_url || ''}
                    alt={item.alt_text || item.original_filename}
                    className="h-full w-full object-cover"
                    loading="lazy"
                  />
                  <span className="absolute inset-0 hidden items-center justify-center bg-foreground/30 group-hover:flex">
                    <Check className="h-6 w-6 text-white" />
                  </span>
                  <AiDisclosureBadge value={item.ai_disclosure} />
                </button>
              ))}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
    {/* Bewusst außerhalb des Picker-Dialogs: verschachtelte Radix-Dialoge
        streiten sich um den Fokus. */}
    <AiDisclosureDialog
      open={!!pendingUpload}
      filename={pendingUpload?.title || pendingUpload?.original_filename}
      onChoose={(v) => void resolvePendingUpload(v)}
    />
    </>
  );
};

export default MediaPicker;
